import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { NetworkStatus, useApolloClient, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';

import { listPath } from '../crmPaths';
import { formatMoment } from '../contactDetail/contactDetail';
import { EmptyState, LoadingRows, TABLE_SX } from '../manage/ManageParts';
import { people } from '../tagging/tagging';
import {
  adminName,
  batchCounts,
  BatchRecord,
  describeBatchError,
  describeUndo,
  forgetBatchData,
  isWorking,
  KIND_LABELS,
  progressOf,
  PROGRESS_POLL_MS,
} from './batchUpdate';
import { CRM_BATCH_UPDATES, CRM_RESUME_BATCH_UPDATE, CRM_UNDO_BATCH_UPDATE } from './batchUpdate.queries';

interface BatchHistoryProps {
  /** One list's batches; every batch when absent. */
  segmentId?: number;
  /** Undone (true), not undone (false), or both. */
  undone?: boolean;
  take?: number;
  /** Shown when there are none. */
  emptyText?: string;
  /** Called after an undo: what the screen around it shows is stale. */
  onUndone?: () => void;
}

interface Page {
  crmBatchUpdates: { total: number; hasNextPage: boolean; endCursor: string | null; items: Array<BatchRecord> };
}

/** Undoes one batch, after saying what that does. */
const UndoDialog = ({ batch, onClose, onUndone }: { batch: BatchRecord; onClose: () => void; onUndone: (b: BatchRecord) => void }) => {
  const client = useApolloClient();
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<unknown>();

  const run = async () => {
    setRunning(true);
    setError(undefined);
    try {
      const { data } = await client.mutate<{ crmUndoBatchUpdate: BatchRecord }>({ mutation: CRM_UNDO_BATCH_UPDATE, variables: { id: batch.id } });
      onUndone(data!.crmUndoBatchUpdate);
    } catch (err) {
      setError(err);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open onClose={() => !running && onClose()} fullWidth maxWidth="xs" aria-labelledby="crm-undo-title">
      <DialogTitle id="crm-undo-title">Undo batch #{batch.id}?</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {batch.summary}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          It changed {people(batch.changed)}. {batch.undoNote}
        </Typography>
        {error !== undefined && <Alert severity="error">{describeBatchError(error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={running}>
          Keep it
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={run}
          disabled={running}
          startIcon={running ? <CircularProgress size={16} color="inherit" aria-label="Working" /> : undefined}
        >
          Undo
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * The batch updates run, newest first (card 20): what each did, to how many, by
 * whom and from which list, and an undo for each that can be. "Show more" asks
 * the next page by cursor. Nothing polls.
 */
export const BatchHistory = ({ segmentId, undone, take = 25, emptyText, onUndone }: BatchHistoryProps) => {
  const client = useApolloClient();
  const [undoing, setUndoing] = useState<BatchRecord | null>(null);
  const [notice, setNotice] = useState<string>();
  const filter = {
    ...(segmentId !== undefined ? { segmentId } : {}),
    ...(undone !== undefined ? { undone } : {}),
  };
  const { data, previousData, loading, error, fetchMore, networkStatus, refetch } = useQuery<Page>(CRM_BATCH_UPDATES, {
    variables: { filter, take },
    notifyOnNetworkStatusChange: true,
  });
  const shown = (data ?? previousData)?.crmBatchUpdates;
  const loadingMore = networkStatus === NetworkStatus.fetchMore;
  // While a batch on the page is working, ask again every two seconds; stop once none is.
  const working = Boolean(shown?.items.some(isWorking));
  useEffect(() => {
    if (!working) return undefined;
    const timer = setInterval(() => refetch(), PROGRESS_POLL_MS);
    return () => clearInterval(timer);
  }, [working, refetch]);
  const [resuming, setResuming] = useState<number | null>(null);
  const resume = async (b: BatchRecord) => {
    setResuming(b.id);
    try {
      await client.mutate({ mutation: CRM_RESUME_BATCH_UPDATE, variables: { id: b.id } });
      setNotice(`Batch #${b.id} resumed.`);
      refetch();
    } catch (err) {
      setNotice(describeBatchError(err));
    } finally {
      setResuming(null);
    }
  };

  const more = () =>
    fetchMore({
      variables: { after: shown?.endCursor },
      updateQuery: (prev, { fetchMoreResult }) => ({
        crmBatchUpdates: {
          ...fetchMoreResult.crmBatchUpdates,
          items: [...prev.crmBatchUpdates.items, ...fetchMoreResult.crmBatchUpdates.items],
        },
      }),
    });

  const undid = (b: BatchRecord) => {
    setUndoing(null);
    setNotice(`Batch #${b.id} undone. ${describeUndo(b)}`);
    forgetBatchData(client.cache);
    refetch();
    onUndone?.();
  };

  if (error && !shown) {
    return (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => refetch()}>Try again</Button>}>
        {describeBatchError(error)}
      </Alert>
    );
  }

  if (shown && shown.items.length === 0) {
    return (
      <EmptyState
        icon={<HistoryOutlinedIcon />}
        title="No batch updates yet"
        text={emptyText ?? 'Select people on the contact list, or open a list, and choose Batch update.'}
      />
    );
  }

  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      {notice && (
        <Alert severity="success" onClose={() => setNotice(undefined)} role="status">
          {notice}
        </Alert>
      )}
      <TableContainer sx={TABLE_SX}>
        <Table size="small" aria-label="Batch updates">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>What</TableCell>
              <TableCell>People</TableCell>
              {segmentId === undefined && <TableCell>From</TableCell>}
              <TableCell>By</TableCell>
              <TableCell align="right">Undo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!shown && <LoadingRows columns={segmentId === undefined ? 6 : 5} rows={4} />}
            {shown?.items.map((b) => (
              <TableRow key={b.id} hover sx={b.undoneAt ? { '& td': { color: 'text.secondary' } } : undefined}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {formatMoment(b.createdAt)}
                  <Typography variant="caption" color="text.disabled" component="div">
                    #{b.id}
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 200, maxWidth: 380 }}>
                  <Chip size="small" variant="outlined" label={KIND_LABELS[b.kind] ?? b.kind} sx={{ height: 20, fontSize: '0.6875rem', mb: 0.5 }} />
                  <Typography variant="body2" sx={{ wordBreak: 'break-word', ...(b.undoneAt ? { textDecoration: 'line-through' } : {}) }}>
                    {b.summary}
                  </Typography>
                  {b.note && (
                    <Typography variant="caption" color="text.secondary" component="p" sx={{ fontStyle: 'italic' }}>
                      “{b.note}”
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  <Typography variant="body2">{batchCounts(b)}</Typography>
                </TableCell>
                {segmentId === undefined && (
                  <TableCell sx={{ minWidth: 120, maxWidth: 200 }}>
                    {b.segment ? (
                      <Link component={RouterLink} to={listPath(b.segment.id)} underline="hover">
                        {b.segment.name}
                      </Link>
                    ) : b.segmentId ? (
                      <Typography variant="body2" color="text.secondary">
                        A deleted list
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Contact list
                      </Typography>
                    )}
                  </TableCell>
                )}
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{adminName(b.createdBy) || '—'}</TableCell>
                <TableCell align="right" sx={{ width: 200, minWidth: 140 }}>
                  {isWorking(b) ? (
                    <Typography variant="caption" color="primary" component="div">
                      {b.status === 'UNDOING' ? 'Undoing' : 'Running'} · {progressOf(b).text}
                    </Typography>
                  ) : b.status === 'FAILED' ? (
                    <Tooltip title={b.error ?? ''}>
                      <Box>
                        <Typography variant="caption" color="error" component="div">
                          Stopped part way
                        </Typography>
                        <Button size="small" onClick={() => resume(b)} disabled={resuming === b.id} aria-label={`Resume batch ${b.id}`}>
                          Resume
                        </Button>
                        {b.canUndo && (
                          <Button size="small" color="warning" onClick={() => setUndoing(b)} aria-label={`Undo batch ${b.id}`}>
                            Undo
                          </Button>
                        )}
                      </Box>
                    </Tooltip>
                  ) : b.undoneAt ? (
                    <Tooltip title={describeUndo(b)}>
                      <Typography variant="caption" color="text.secondary" component="div">
                        Undone {formatMoment(b.undoneAt)}
                        {b.undoneBy ? ` by ${adminName(b.undoneBy)}` : ''}
                      </Typography>
                    </Tooltip>
                  ) : b.canUndo ? (
                    <Button size="small" color="warning" onClick={() => setUndoing(b)} aria-label={`Undo batch ${b.id}`}>
                      Undo
                    </Button>
                  ) : (
                    <Tooltip title={b.undoNote}>
                      <Typography variant="caption" color="text.secondary">
                        Nothing to undo
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {shown && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {shown.items.length.toLocaleString('en-US')} of {shown.total.toLocaleString('en-US')}
          </Typography>
          {shown.hasNextPage && (
            <Button size="small" onClick={more} disabled={loadingMore || loading}>
              {loadingMore ? 'Loading…' : 'Show more'}
            </Button>
          )}
        </Box>
      )}
      {undoing && <UndoDialog batch={undoing} onClose={() => setUndoing(null)} onUndone={undid} />}
    </Box>
  );
};
